import type { ReactNode } from "react";
import { cx } from "shared/utils";
import { Show } from "shared/components/helpers/Show";
import {
  type BannerBorders,
  type BannerButtonGroupPosition,
  type BannerVariant,
  type ButtonProps,
  getBorderClass,
  getBorderWidth
} from "./utils";
import { BannerBodySection } from "./components/BannerBodySection";
import type { BannerBodySectionProps } from "./components/BannerBodySection";
import { BannerButtonGroup } from "./components/BannerButtonGroup";
import type { BannerButtonGroupProps } from "./components/BannerButtonGroup";

export type {
  ButtonProps,
  BannerVariant,
  BannerBorders,
  BannerButtonGroupPosition
} from "./utils";
export type BannerTextTheme = "base" | "colorful";

export type BannerProps = {
  variant?: BannerVariant;
  textTheme?: BannerTextTheme;
  buttonGroupPosition?: BannerButtonGroupPosition;
  HugeIcon?: ReactNode;
  title?: string;
  body?: string | ReactNode;

  borders?: BannerBorders;
  borderWidth?: number;

  primaryButton?: ButtonProps;
  secondaryButton?: ButtonProps;

  buttonGroupClassName?: string;
  titleClassName?: string;
  bodyClassName?: string;
  className?: string;
};

type VariantConfig = {
  containerBg: string;
  accentColor: string;
  linkHover: string;
};

const VARIANT_CONFIG: Record<BannerVariant, VariantConfig> = {
  base: {
    containerBg: "bg-background-1000",
    accentColor: "text-primary-500",
    linkHover: "hover:text-primary-600"
  },
  danger: {
    containerBg: "bg-danger-50",
    accentColor: "text-danger-600",
    linkHover: "hover:text-danger-700"
  },
  warning: {
    containerBg: "bg-warning-50",
    accentColor: "text-warning-600",
    linkHover: "hover:text-warning-700"
  },
  primary: {
    containerBg: "bg-primary-20",
    accentColor: "text-primary-500",
    linkHover: "hover:text-primary-600"
  },
  success: {
    containerBg: "bg-success-50",
    accentColor: "text-success-700",
    linkHover: "hover:text-success-800"
  }
};

export const Banner = ({
  variant = "base",
  textTheme = "base",
  buttonGroupPosition = "right",
  HugeIcon,
  title,
  body,
  borders = "card",
  borderWidth = 1,
  primaryButton,
  secondaryButton,
  buttonGroupClassName,
  className,
  bodyClassName
}: BannerProps) => {
  const config = VARIANT_CONFIG[variant];

  const hasButtons = primaryButton || secondaryButton;
  const isButtonsRight = buttonGroupPosition === "right";

  const titleColor =
    textTheme === "colorful" ? config.accentColor : "text-content-1000";
  const bodyColor =
    textTheme === "colorful" ? config.accentColor : "text-content-500";

  const showBorder = borders !== "none" && borderWidth > 0;
  const borderClass = showBorder ? getBorderClass(borders) : "";
  const borderStyle = getBorderWidth(borders, borderWidth);

  const buttonGroupProps: BannerButtonGroupProps = {
    variant,
    buttonGroupPosition,
    primaryButton,
    secondaryButton,
    className: buttonGroupClassName
  };

  const bodySectionProps: BannerBodySectionProps = {
    title,
    body,
    titleColor,
    bodyColor,
    bodyClassName,
    hasButtons: !!hasButtons,
    buttonGroupProps
  };

  return (
    <div
      className={cx(
        "w-full p-4",
        config.containerBg,
        borderClass,
        borders === "card" ? "rounded-lg" : ""
      )}
      style={borderStyle}
    >
      <div
        className={cx(
          "flex",
          isButtonsRight ? "flex-row items-center gap-4" : "flex-col",
          className
        )}
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Show if={!!HugeIcon}>
            <div className="flex mt-[2px] h-5 w-5 shrink-0 items-center justify-center">
              {HugeIcon}
            </div>
          </Show>
          <BannerBodySection {...bodySectionProps} />
        </div>
        <Show if={!!hasButtons && isButtonsRight}>
          <BannerButtonGroup {...buttonGroupProps} />
        </Show>
      </div>
    </div>
  );
};
